// Login.jsx
import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  IconButton,
  InputAdornment,
  Fade,
  Snackbar,
  Alert,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ForgotPasswordModal from '../components/ForgotPasswordModal';

const theme = createTheme({
  palette: {
    mode: 'light',
    background: { default: '#ffffff', paper: '#ffffff' },
    primary: { main: '#C4B08C' },
    text: { primary: '#111', secondary: '#555' },
    error: { main: '#d32f2f' },
  },
  typography: { fontFamily: '"Inter", sans-serif' },
});
const API_URL = process.env.REACT_APP_API_URL;

const Login = () => {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  const [alert, setAlert] = useState({
    open: false,
    severity: 'success' as 'success' | 'error',
    message: '',
  });

  const navigate = useNavigate();

  const handle = (e: any) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const validate = () => {
    let newErrors: any = {};
    if (!form.email) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = 'Invalid email address';
    if (!form.password) newErrors.password = 'Password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const submit = async (e: any) => {
  e.preventDefault();
  if (!validate()) return;

  setLoading(true);
  try {
    const res = await fetch(`${API_URL}/access/signin`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: form.email,
        password: form.password,
      }),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Server error");

    // Save token and role in localStorage
    localStorage.setItem("token", data.token);
    localStorage.setItem("role", data.user.role);
    localStorage.setItem("email", data.user.email);
    localStorage.setItem("userId", data.user.id);


    setAlert({
      open: true,
      severity: "success",
      message: "Login successful!",
    });

    setTimeout(() => navigate("/"), 1000);
  } catch (err) {
    console.error(err);
    setAlert({
      open: true,
      severity: "error",
      message: err instanceof Error ? err.message : "Something went wrong",
    });
  } finally {
    setLoading(false);
  }
};


  return (
    <ThemeProvider theme={theme}>
      <Fade in timeout={700}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            px: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 420,
              p: 4,
              borderRadius: 5,
              boxShadow: '0 8px 40px rgba(0,0,0,.08)',
            }}
          >
            <Typography
              variant="h4"
              fontWeight={700}
              textAlign="center"
              mb={0.5}
            >
              Welcome Back
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              mb={3}
            >
              Sign in to continue
            </Typography>

            <Box component="form" onSubmit={submit} noValidate>
              <TextField
                fullWidth
                variant="standard"
                label="Email"
                type="email"
                name="email"
                value={form.email}
                onChange={handle}
                margin="dense"
                InputProps={{ disableUnderline: true, sx: fieldSx }}
              />
              {errors.email && <Alert severity="error">{errors.email}</Alert>}

              <TextField
                fullWidth
                variant="standard"
                label="Password"
                type={show ? 'text' : 'password'}
                name="password"
                value={form.password}
                onChange={handle}
                margin="dense"
                InputProps={{
                  disableUnderline: true,
                  sx: fieldSx,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShow(!show)} edge="end">
                        {show ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {errors.password && <Alert severity="error">{errors.password}</Alert>}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disableElevation
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.4,
                  fontWeight: 600,
                  borderRadius: 3,
                  bgcolor: 'primary.main',
                  color: '#000',
                  '&:hover': { bgcolor: '#b39d77' },
                }}
              >
                {loading ? 'Processing...' : 'Sign In'}
              </Button>

              {/* Forgot Password Link */}
              <Box sx={{ textAlign: 'center', mt: 1, mb: 2 }}>
                <Link
                  onClick={() => setForgotPasswordOpen(true)}
                  sx={{
                    color: 'text.secondary',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  Mot de passe oublié ?
                </Link>
              </Box>
            </Box>

            <Typography variant="body2" textAlign="center">
              Don’t have an account?{' '}
              <Link
                onClick={() => navigate('/register')}
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Register
              </Link>
            </Typography>
          </Paper>

          {/* Forgot Password Modal */}
          <ForgotPasswordModal
            open={forgotPasswordOpen}
            onClose={() => setForgotPasswordOpen(false)}
            onSuccess={(message) => {
              setAlert({ open: true, severity: 'success', message });
              setForgotPasswordOpen(false);
            }}
            onError={(message) => {
              setAlert({ open: true, severity: 'error', message });
            }}
          />

          {/* Snackbar for success/error */}
          <Snackbar
            open={alert.open}
            autoHideDuration={4000}
            onClose={() => setAlert({ ...alert, open: false })}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity={alert.severity} sx={{ width: '100%' }}>
              {alert.message}
            </Alert>
          </Snackbar>
        </Box>
      </Fade>
    </ThemeProvider>
  );
};

export default Login;

const fieldSx = {
  px: 2,
  py: 1.2,
  borderRadius: 3,
  fontSize: 15,
  bgcolor: '#f5f5f5',
};
